// apps/dashboard/src/stores/language.store.ts
// Manages the active UI language — syncs with i18next and localStorage.
// Optionally persists to the server via PATCH /tenants/:tenantId/users/:userId.

import i18n, { type SupportedLanguage, SUPPORTED_LANGUAGES } from '@/i18n';
import { useAuthStore } from '@/stores/auth.store';
import { create } from 'zustand';

interface LanguageState {
  language: SupportedLanguage;
  /** Switch language locally (i18n + localStorage). Pass persist=true to also save via API. */
  setLanguage: (lang: SupportedLanguage, persist?: boolean) => Promise<void>;
}

function isSupported(lang: string): lang is SupportedLanguage {
  return (SUPPORTED_LANGUAGES as readonly string[]).includes(lang);
}

/** Detect the initial language already resolved by i18next (from localStorage or navigator). */
function resolveInitialLanguage(): SupportedLanguage {
  const stored = localStorage.getItem('omniflow_language');
  if (stored && isSupported(stored)) return stored;
  const nav = navigator.language;
  if (isSupported(nav)) return nav;
  // Try prefix match: en-GB → en-US, pt → pt-BR
  const prefix = nav.split('-')[0];
  const match = (SUPPORTED_LANGUAGES as readonly string[]).find((l) => l.startsWith(prefix));
  if (match && isSupported(match)) return match;
  return 'pt-BR';
}

export const useLanguageStore = create<LanguageState>()((set) => ({
  language: resolveInitialLanguage(),

  setLanguage: async (lang, persist = false) => {
    // 1. Apply locally
    await i18n.changeLanguage(lang);
    localStorage.setItem('omniflow_language', lang);
    set({ language: lang });

    // 2. Optionally persist to server
    if (!persist) return;
    const { token, user } = useAuthStore.getState();
    if (!token || !user) return;

    try {
      await fetch(`/api/tenants/${user.tenantId}/users/${user.id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ language: lang }),
      });
    } catch {
      // Persistence failure is non-fatal — local preference is already applied
    }
  },
}));
